import React, { useState } from 'react';
import { LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';
import { useSidebar } from '../../contexts/SidebarContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface LogoutButtonProps {
  className?: string;
  isCollapsed?: boolean;
}

const LogoutButton = ({ className, isCollapsed: propIsCollapsed }: LogoutButtonProps) => {
  const { logout, loading } = useAuth();
  const { isCollapsed: contextIsCollapsed } = useSidebar();
  const { t } = useLanguage();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Use prop if provided, otherwise use context
  const isCollapsed = propIsCollapsed !== undefined ? propIsCollapsed : contextIsCollapsed;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isLoading = loading || isLoggingOut;

  return (
    <button 
      onClick={handleLogout}
      disabled={isLoading}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
        "hover:bg-white/10 active:scale-[0.98] text-slate-300",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
      title={isCollapsed ? t('common.signOut') : undefined}
    >
      <div className={cn(
        "w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0",
        "bg-transparent"
      )}>
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-red-400" />
        ) : (
          <LogOut className="w-5 h-5 text-red-400" />
        )}
      </div>
      {!isCollapsed && (
        <span className="font-medium text-sm whitespace-nowrap">
          {isLoading ? t('common.signingOut') : t('common.signOut')}
        </span>
      )}
    </button>
  );
};

export default LogoutButton;